import { useMemo } from 'react';
import { useLocation } from 'react-router';
import { useMutation } from '@tanstack/react-query';

import { Response } from '~/common/services/requests';
import {
  ApiErrorResponse,
  convertStatusToRequestState,
  NonPaginatedResponse,
} from '../../../../../../api';
import { ENVIRONMENT } from '../../../../../../constants';
import { RefreshTokensResponse } from '../../../../../../types';
import { decodeJwtSub } from '../../../../../../utils/authentication';
import { logger } from '../../../../../../utils/logger';
import { TransformerSet } from '../../../../../RequestsContext/constants';
import { addTransformersToConfig } from '../../../../../RequestsContext/hooks/usePlatformRequest/logic/addTransformersToConfig';
import { useSetReturnUrl } from '../../../../../ReturnUrlContext';
import { useAuthApi } from '../../../../hooks/useAuthApi';
import { useAuthData } from '../../../../hooks/useAuthData';
import { useAuthorizedInceptRequest } from '../../../../hooks/useAuthorizedInceptRequest';
import { refreshedSession, unauthenticatedSession } from '../../../../session';
import { useIsTokenValid } from './hooks/useIsTokenValid';
import { useTokensRefresher } from './hooks/useTokensRefresher';

export const useAuthTokensRefreshManagement = () => {
  const tokens = useAuthData();
  const { setSession } = useAuthApi();
  const performAuthorizedRequest = useAuthorizedInceptRequest();
  const isTokenValid = useIsTokenValid();
  const setReturnUrl = useSetReturnUrl();
  const location = useLocation();

  // We use a mutation here to handle the token refresh process.
  // we cannot use usePlatformRequest as we are above it the RequestsProvider
  const { mutate: requestTokenRefresh, status } = useMutation<
    Awaited<Response<NonPaginatedResponse<RefreshTokensResponse>>>,
    ApiErrorResponse
  >({
    // Refresh runs above the RequestsProvider, so it does not inherit the
    // platform request's transforms; apply them here so the response body is
    // JSON-parsed and camelCased (expires_at -> expiresAt) like every other
    // response. The payload is wrapped in a data envelope, hence data.data.
    mutationFn: async () =>
      performAuthorizedRequest<NonPaginatedResponse<RefreshTokensResponse>>(
        addTransformersToConfig(
          {
            method: 'POST',
            url: `${ENVIRONMENT.apiUrl}/auth/refresh`,
          },
          TransformerSet.Default,
        ),
      ),
    cacheTime: 0,
    onError: () => {
      logger.log('token refresh failed, logging out');
      setReturnUrl(
        location.pathname + location.search,
        tokens?.jwt ? decodeJwtSub(tokens.jwt) : undefined,
      );
      setSession(unauthenticatedSession());
    },
    onSuccess: (response) => {
      const { token, expiresAt } = response.data.data;
      // Preserve the session's status and its 2FA token across the refresh:
      // an authenticated session stays authenticated, an awaiting one stays
      // awaiting, with the new first-factor token/expiry.
      setSession(refreshedSession(tokens, token, expiresAt));
    },
  });

  useTokensRefresher(tokens, requestTokenRefresh);

  const dataValue = useMemo(
    () => ({
      isTokenValid,
      tokenRefreshState: convertStatusToRequestState(status),
    }),
    [isTokenValid, status],
  );

  return {
    dataValue,
  };
};
